// @flow
import {
  createSlice,
  createEntityAdapter,
  createAsyncThunk,
  createSelector
} from '@reduxjs/toolkit';
import type { GetListingPhotosUploadItem } from '@justpark/api/src/types';
import getListingUploadPhotos from '@justpark/api/src/requests/getListingUploadPhotos';
import uploadPhotoRequest from '@justpark/api/src/requests/uploadPhoto';
import syncListingPhotosRequest from '@justpark/api/src/requests/syncListingPhotos';
import fileReaderPromise from '@justpark/helpers/src/fileReaderPromise/fileReaderPromise';
import getRequestError from '@justpark/api/src/helpers/getRequestError';
import rotateListingUploadPhotoRequest from '@justpark/api/src/requests/listingUploadPhotoRotate';

const sliceName = 'listingPhotos';

type ListingPhotoItem = {
  id: string,
  key: string,
  url: string,
  width: number | null,
  height: number | null,
  rotation: number,
  pendingDeletion: boolean,
  pendingUpload: boolean,
  pendingRotation: boolean
};

type CustomInitialState = {
  loadingState: 'loading' | 'loaded' | 'failed' | '',
  error: string,
  uploadError: string,
  deleting: boolean
};

const customInitialState: CustomInitialState = {
  loadingState: '',
  error: '',
  uploadError: '',
  deleting: false
};

const mapGetListingUploadsItemToEntity = (
  item: GetListingPhotosUploadItem,
  size?: 'thumb' | 'small' | 'normal' | 'square' = 'thumb'
) => {
  const {
    id,
    jsonData: {
      [size]: { url, width, height }
    },
    tag
  } = item;
  return {
    id,
    key: id,
    url,
    width,
    height,
    rotation: 0,
    pendingUpload: false,
    pendingRotation: false,
    tag
  };
};

export const getListingUploads = createAsyncThunk(
  `${sliceName}/getListingUploads`,
  async (
    {
      listingId,
      size = 'thumb'
    }: { listingId: number, size?: 'thumb' | 'small' | 'normal' | 'square' },
    { extra: { jpApiClient }, rejectWithValue }
  ): any => {
    try {
      const request = getListingUploadPhotos(listingId);
      const response = await jpApiClient(request);

      return response.data.uploads.map((item) =>
        mapGetListingUploadsItemToEntity(item, size)
      );
    } catch (err) {
      return rejectWithValue(getRequestError(err));
    }
  }
);

const uploadListingPhotoPreview = createAsyncThunk(
  `${sliceName}/uploadListingPhotoPreview`,
  async (payload) => {
    const { file, requestId, tag } = payload;

    const result = await fileReaderPromise(file);

    return {
      url: result,
      id: requestId,
      key: requestId,
      width: 800,
      height: 600,
      rotation: 0,
      pendingUpload: true,
      tag
    };
  }
);

export const uploadListingPhoto = createAsyncThunk(
  `${sliceName}/uploadListingPhoto`,
  async (
    payload: { listingId: number, file: any, tag?: string },
    { requestId, dispatch, extra: { jpApiClient }, rejectWithValue }
  ): any => {
    const { listingId, file, tag = null } = payload;

    const previewPromise = dispatch(
      uploadListingPhotoPreview({ file, requestId, tag })
    );

    try {
      const request = uploadPhotoRequest(listingId, file, 'listing', tag);
      const response = await jpApiClient(request);
      await previewPromise;

      return response.data.data;
    } catch (err) {
      return rejectWithValue(getRequestError(err));
    }
  }
);

export const syncListingPhotos = createAsyncThunk(
  `${sliceName}/syncListingPhotos`,
  async (
    listingId,
    { getState, extra: { jpApiClient }, rejectWithValue }
  ): any => {
    const { ids } = getState()[sliceName];

    try {
      const request = syncListingPhotosRequest(listingId, ids);
      await jpApiClient(request);

      return null;
    } catch (err) {
      return rejectWithValue(getRequestError(err));
    }
  }
);

export const rotateListingUploadPhoto = createAsyncThunk(
  `${sliceName}/rotateListingUploadPhoto`,
  async (
    payload: { listingId: number, uploadId: string },
    { extra: { jpApiClient } }
  ): any => {
    const { listingId, uploadId } = payload;
    const request = rotateListingUploadPhotoRequest(listingId, uploadId, 90);
    await jpApiClient(request);
  }
);

const photosAdapter = createEntityAdapter<ListingPhotoItem>();

const initialState = photosAdapter.getInitialState(customInitialState);

export const {
  selectAll: getAllRawListingUploads,
  selectTotal: selectListingPhotosTotal
} = photosAdapter.getSelectors((state) => state[sliceName]);

const { selectById } = photosAdapter.getSelectors();

const listingPhotosSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    reset: () => ({
      ...initialState
    }),
    deleteListingUploadPhoto: (state, action) =>
      photosAdapter.removeOne(
        { ...state, deleting: true },
        action.payload.uploadId
      )
  },
  extraReducers: (builder) => {
    builder.addCase(getListingUploads.pending, (state) => ({
      ...state,
      loadingState: 'loading'
    }));
    builder.addCase(getListingUploads.fulfilled, (state, action) =>
      photosAdapter.setAll(
        {
          ...state,
          loadingState: 'loaded'
        },
        action.payload
      )
    );
    builder.addCase(getListingUploads.rejected, (state, action) => ({
      ...state,
      loadingState: 'failed',
      error: action.payload
    }));
    builder.addCase(uploadListingPhotoPreview.fulfilled, (state, action) =>
      photosAdapter.addOne(state, action.payload)
    );
    builder.addCase(uploadListingPhoto.fulfilled, (state, action) => {
      const {
        meta: { requestId }
      } = action;

      const item = selectById(state, requestId);

      if (!item) {
        return state;
      }

      return photosAdapter.updateOne(
        {
          ...state,
          uploadError: ''
        },
        {
          id: requestId,
          changes: {
            id: action.payload.id.toString(),
            pendingUpload: false
          }
        }
      );
    });
    builder.addCase(uploadListingPhoto.rejected, (state, action) => {
      const {
        meta: { requestId },
        payload
      } = action;

      const item = selectById(state, requestId);

      if (!item) {
        return state;
      }

      return photosAdapter.removeOne(
        {
          ...state,
          uploadError: payload
        },
        requestId
      );
    });
    builder.addCase(rotateListingUploadPhoto.pending, (state, action) => {
      const { uploadId } = action.meta.arg;

      const item = selectById(state, uploadId);
      const existingRotation = item.rotation;

      return photosAdapter.updateOne(state, {
        id: uploadId,
        changes: {
          rotation: existingRotation + 90,
          pendingRotation: true
        }
      });
    });
    builder.addCase(rotateListingUploadPhoto.fulfilled, (state, action) => {
      const { uploadId } = action.meta.arg;

      return photosAdapter.updateOne(state, {
        id: uploadId,
        changes: {
          pendingRotation: false
        }
      });
    });
  }
});

export default listingPhotosSlice;

export const { reset, deleteListingUploadPhoto } = listingPhotosSlice.actions;

export const getAllListingUploads = createSelector(
  getAllRawListingUploads,
  (uploads) => uploads.filter((item) => item.pendingDeletion !== true)
);

export const getAllEVListingUploads = createSelector(
  getAllListingUploads,
  (uploads) => uploads.filter((item) => item.tag === 'ev')
);

export const selectListingUploadsIsUploadPending = createSelector(
  getAllRawListingUploads,
  (uploads) =>
    uploads.find(
      (item) => item.pendingRotation === true || item.pendingUpload === true
    ) !== undefined
);

export const selectListingPhotosRequiresLoading = (state) => {
  const { loadingState } = state[sliceName];

  return loadingState === '';
};

export const selectListingPhotosIsLoading = (state) => {
  const { loadingState } = state[sliceName];

  return loadingState === 'loading';
};

export const selectListingPhotosHasLoaded = (state) => {
  const { loadingState } = state[sliceName];

  return loadingState === 'loaded';
};

export const selectListingPhotosLoadingError = (state) => {
  const { error } = state[sliceName];

  return error;
};

export const selectListingPhotosUploadError = (state) => {
  const { uploadError } = state[sliceName];

  return uploadError;
};

export const selectListingPhotosIsSubmitDisabled = createSelector(
  getAllEVListingUploads,
  (evPhotos) => !evPhotos.length
);
